import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const BrandContext = createContext();

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};

export const BrandProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [brands, setBrands] = useState([]);
  const [allBrands, setAllBrands] = useState([]); // Todas as marcas (admin/mensure)
  const [activeBrand, setActiveBrandState] = useState(() => {
    const saved = localStorage.getItem('activeBrandId');
    return saved || null;
  });
  const [activeBrandData, setActiveBrandData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false); // Modo curadoria
  const [brandVersion, setBrandVersion] = useState(0); // Versão que muda ao trocar marca

  // Verificar se e admin/mensure (acesso tecnico completo)
  const isAdminOrMensure = user?.role === 'admin' || user?.role === 'mensure';

  // Verificar se pode fazer curadoria (admin, mensure, curator)
  const canCurate = user?.role === 'admin' || user?.role === 'mensure' || user?.role === 'curator';

  // Buscar marcas do usuario
  const fetchBrands = useCallback(async () => {
    if (!token || !user) {
      setBrands([]);
      setAllBrands([]);
      setActiveBrandData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Buscar marcas proprias
      const response = await api.get('/brands');
      const brandsList = response.data.data || [];
      setBrands(brandsList);

      // Se pode fazer curadoria, buscar TODAS as marcas
      if (canCurate) {
        try {
          const allResponse = await api.get('/brands/all');
          const allBrandsList = allResponse.data.data || [];
          setAllBrands(allBrandsList);
        } catch (err) {
          console.warn('Erro ao buscar todas as marcas (curadoria):', err);
          setAllBrands([]);
        }
      } else {
        setAllBrands([]);
      }

      // Se nao tem marca ativa, usar a padrao
      if (!activeBrand && brandsList.length > 0) {
        const defaultBrand = brandsList.find(b => b.isDefault) || brandsList[0];
        setActiveBrandState(defaultBrand._id);
        setActiveBrandData(defaultBrand);
        setIsImpersonating(false);
        localStorage.setItem('activeBrandId', defaultBrand._id);
      } else if (activeBrand) {
        // Buscar dados da marca ativa (pode ser propria ou de outro usuario)
        let brand = brandsList.find(b => b._id === activeBrand);
        let impersonating = false;

        // Se nao encontrou nas proprias e pode fazer curadoria, buscar em todas
        if (!brand && canCurate) {
          const allResponse = await api.get('/brands/all');
          const allBrandsList = allResponse.data.data || [];
          brand = allBrandsList.find(b => b._id === activeBrand);
          if (brand) {
            impersonating = true;
          }
        }

        if (brand) {
          setActiveBrandData(brand);
          setIsImpersonating(impersonating);
        } else if (brandsList.length > 0) {
          // Marca salva nao existe mais, usar padrao
          const defaultBrand = brandsList.find(b => b.isDefault) || brandsList[0];
          setActiveBrandState(defaultBrand._id);
          setActiveBrandData(defaultBrand);
          setIsImpersonating(false);
          localStorage.setItem('activeBrandId', defaultBrand._id);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Erro ao buscar marcas:', err);
      setError(err.response?.data?.message || 'Erro ao buscar marcas');
    } finally {
      setLoading(false);
    }
  }, [token, user, activeBrand, canCurate]);

  // Buscar marcas quando usuario logar
  useEffect(() => {
    if (token && user) {
      fetchBrands();
    } else {
      setBrands([]);
      setActiveBrandData(null);
      setLoading(false);
    }
  }, [token, user, fetchBrands]);

  // Configurar header x-brand-id no axios quando marca ativa mudar
  useEffect(() => {
    if (activeBrand) {
      api.defaults.headers.common['x-brand-id'] = activeBrand;
    } else {
      delete api.defaults.headers.common['x-brand-id'];
    }
  }, [activeBrand]);

  // Trocar marca ativa
  const setActiveBrand = useCallback((brandId) => {
    // Primeiro verificar nas marcas proprias
    let brand = brands.find(b => b._id === brandId);
    let impersonating = false;

    // Se nao encontrou nas proprias e pode fazer curadoria, buscar em todas
    if (!brand && canCurate && allBrands.length > 0) {
      brand = allBrands.find(b => b._id === brandId);
      if (brand) {
        impersonating = true;
      }
    }

    if (brand) {
      setActiveBrandState(brandId);
      setActiveBrandData(brand);
      setIsImpersonating(impersonating);
      setBrandVersion(v => v + 1); // Incrementa versão para forçar re-render
      localStorage.setItem('activeBrandId', brandId);
      window.dispatchEvent(new CustomEvent('active-brand-changed', { detail: { brandId } }));

      if (impersonating) {
        console.log(`[CURADORIA] Modo curadoria ativado para marca: ${brand.name}`);
      }
    }
  }, [brands, allBrands, canCurate]);

  // Criar nova marca
  const createBrand = useCallback(async (data) => {
    try {
      const response = await api.post('/brands', data);
      const newBrand = response.data.data;
      setBrands(prev => [...prev, newBrand]);
      return { success: true, data: newBrand };
    } catch (err) {
      console.error('Erro ao criar marca:', err);
      return {
        success: false,
        message: err.response?.data?.message || 'Erro ao criar marca'
      };
    }
  }, []);

  // Atualizar marca
  const updateBrand = useCallback(async (brandId, data) => {
    try {
      const response = await api.put(`/brands/${brandId}`, data);
      const updatedBrand = response.data.data;
      setBrands(prev => prev.map(b => b._id === brandId ? updatedBrand : b));

      // Atualizar dados da marca ativa se for a mesma
      if (activeBrand === brandId) {
        setActiveBrandData(updatedBrand);
      }

      return { success: true, data: updatedBrand };
    } catch (err) {
      console.error('Erro ao atualizar marca:', err);
      return {
        success: false,
        message: err.response?.data?.message || 'Erro ao atualizar marca'
      };
    }
  }, [activeBrand]);

  // Excluir marca
  const deleteBrand = useCallback(async (brandId) => {
    try {
      await api.delete(`/brands/${brandId}`);
      setBrands(prev => prev.filter(b => b._id !== brandId));

      // Se excluiu a marca ativa, trocar para padrao
      if (activeBrand === brandId) {
        const remainingBrands = brands.filter(b => b._id !== brandId);
        if (remainingBrands.length > 0) {
          const defaultBrand = remainingBrands.find(b => b.isDefault) || remainingBrands[0];
          setActiveBrand(defaultBrand._id);
        }
      }

      return { success: true };
    } catch (err) {
      console.error('Erro ao excluir marca:', err);
      return {
        success: false,
        message: err.response?.data?.message || 'Erro ao excluir marca'
      };
    }
  }, [activeBrand, brands, setActiveBrand]);

  // Definir marca como padrao
  const setDefaultBrand = useCallback(async (brandId) => {
    try {
      const response = await api.put(`/brands/${brandId}/set-default`);
      const updatedBrand = response.data.data;

      // Atualizar lista de marcas (remover isDefault das outras)
      setBrands(prev => prev.map(b => ({
        ...b,
        isDefault: b._id === brandId
      })));

      return { success: true, data: updatedBrand };
    } catch (err) {
      console.error('Erro ao definir marca padrao:', err);
      return {
        success: false,
        message: err.response?.data?.message || 'Erro ao definir marca padrao'
      };
    }
  }, []);

  // Obter estatisticas da marca
  const getBrandStats = useCallback(async (brandId) => {
    try {
      const response = await api.get(`/brands/${brandId}/stats`);
      return { success: true, data: response.data.data };
    } catch (err) {
      console.error('Erro ao buscar estatisticas:', err);
      return {
        success: false,
        message: err.response?.data?.message || 'Erro ao buscar estatisticas'
      };
    }
  }, []);

  const value = {
    // Estado
    brands,
    allBrands, // Todas as marcas (para curadoria)
    activeBrand,
    activeBrandData,
    loading,
    error,
    isImpersonating, // Modo curadoria ativo
    isAdminOrMensure, // Se usuario e admin/mensure (acesso tecnico)
    canCurate, // Se pode fazer curadoria (admin, mensure, curator)
    brandVersion, // Versão que muda ao trocar marca

    // Acoes
    fetchBrands,
    setActiveBrand,
    createBrand,
    updateBrand,
    deleteBrand,
    setDefaultBrand,
    getBrandStats,

    // Helpers
    hasBrands: brands.length > 0,
    canCreateBrand: true, // Futuramente: verificar limite do plano
  };

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
};

export default BrandContext;
